export type CanonicalSafetyIndicator = {
  key?: string | null;
  id?: string | null;
  level?: string | null;
  severity?: string | null;
  label?: string | null;
  detail?: string | null;
};

const MAX_DAILY_SAFETY_ROWS = 64;

function severityRank(row: CanonicalSafetyIndicator): number {
  const severity = (row.level ?? row.severity ?? "unknown").toLowerCase().trim();
  if (severity === "red" || severity === "danger") return 4;
  if (["amber", "yellow", "caution", "warning"].includes(severity)) return 3;
  if (["unavailable", "unknown", "no-data"].includes(severity)) return 2;
  if (severity === "green" || severity === "clear") return 1;
  return 2;
}

function stableIdentity(row: CanonicalSafetyIndicator): string {
  const key = row.key?.trim();
  if (key) return `key:${key}`;
  const id = row.id?.trim();
  if (id) return `id:${id}`;
  const label = row.label?.trim() || "unlabelled";
  const detail = row.detail?.trim() || "no-detail";
  return `legacy:${label}:${detail}`;
}

export type SafetyAggregationOptions = Readonly<{
  /**
   * Day safety from daylight hours only (WFD-3, SURF-13; design 6.2), for sports whose safety
   * policy is v2 (CONDITIONS_SAFETY_POLICY_V2_SPORTS). One entry per entry of `hourly`: an hour
   * is left out only when its entry is exactly `false`, so a missing or unknown daylight flag
   * keeps the hour (a hazard is never dropped as night on a guess). When every hour is night
   * (polar night), every hour is aggregated, as without the mask, so a day never reads clear
   * because it has no daylight. Omitted: every hour, byte-identical to the legacy aggregation.
   */
  daylight?: readonly (boolean | null | undefined)[];
}>;

/** Merge hourly safety without allowing row count to override severity authority. */
export function aggregateSafetyIndicators<T extends CanonicalSafetyIndicator>(
  hourly: readonly (readonly T[])[],
  options: SafetyAggregationOptions = {},
): T[] {
  const daylight = options.daylight;
  const daylightHourly = daylight
    ? hourly.filter((_, index) => daylight[index] !== false)
    : hourly;
  const source = daylightHourly.length > 0 ? daylightHourly : hourly;
  const retained = new Map<string, { row: T; rank: number; first: number }>();
  let sequence = 0;
  for (const rows of source) {
    for (const row of rows) {
      const identity = stableIdentity(row);
      const rank = severityRank(row);
      const existing = retained.get(identity);
      if (!existing) retained.set(identity, { row, rank, first: sequence });
      else if (rank > existing.rank) retained.set(identity, { row, rank, first: existing.first });
      sequence++;
    }
  }

  return [...retained.values()]
    .sort((left, right) => right.rank - left.rank || left.first - right.first)
    .slice(0, MAX_DAILY_SAFETY_ROWS)
    .map(({ row }) => row);
}

// ---------------------------------------------------------------------------
// "Storm forecast unavailable" (design 2.5 and 6.2, WP-S0): the one row every safety surface
// emits when the thunderstorm evidence (the weather code) is missing, so a missing code is never
// read as clear sky. Shared so the route, the interim day summary, the page briefing and the
// engines' per-hour v2 safety modules use the same key and wording.
// ---------------------------------------------------------------------------

export const STORM_FORECAST_UNAVAILABLE_KEY = "storm-forecast";
export const STORM_FORECAST_UNAVAILABLE_LABEL = "Storm forecast unavailable";
export const STORM_FORECAST_UNAVAILABLE_DETAIL =
  "The weather-code forecast is missing, so thunderstorm and lightning risk cannot be assessed.";

export type StormForecastUnavailableRow = Readonly<{
  key: typeof STORM_FORECAST_UNAVAILABLE_KEY;
  level: "unavailable";
  severity: "unavailable";
  label: typeof STORM_FORECAST_UNAVAILABLE_LABEL;
  detail: string;
  confidence: "unavailable";
  provenance: "missing-forecast-input";
}>;

/** A fresh "Storm forecast unavailable" row (callers may spread it into their own row type). */
export function stormForecastUnavailableRow(): StormForecastUnavailableRow {
  return {
    key: STORM_FORECAST_UNAVAILABLE_KEY,
    level: "unavailable",
    severity: "unavailable",
    label: STORM_FORECAST_UNAVAILABLE_LABEL,
    detail: STORM_FORECAST_UNAVAILABLE_DETAIL,
    confidence: "unavailable",
    provenance: "missing-forecast-input",
  };
}

/** Whether a provider weather code is present (a finite number); null, undefined and NaN are missing. */
export function weatherCodePresent(code: number | null | undefined): code is number {
  return typeof code === "number" && Number.isFinite(code);
}

/** WMO thunderstorm codes (95, 96, 99): the level every leaf safety function treats as lightning. */
export const THUNDERSTORM_WEATHER_CODE_MIN = 95;
